import type { Language } from './types';

// WHY: uz-UZ through Intl: Chromium prints «M08 16», WebKit «16-avg» — so the Uzbek month names are ours
const UZ_SHORT_MONTHS = ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avg', 'sen', 'okt', 'noy', 'dek'];

// WHY: fa-IR through Intl switches to Jalali + arabext digits — Gregorian month names are ours, the clock goes via en-GB
const FA_MONTHS = [
  'ژانویه', 'فوریه', 'مارس', 'آوریل', 'مه', 'ژوئن',
  'ژوئیه', 'اوت', 'سپتامبر', 'اکتبر', 'نوامبر', 'دسامبر',
];

const DATE_LOCALE: Record<Language, string> = { ru: 'ru-RU', en: 'en-US', uz: 'uz-UZ', fa: 'en-GB', pt: 'pt-BR', tr: 'tr-TR', uk: 'uk-UA', de: 'de-DE', es: 'es-ES', pl: 'pl-PL' };

const NUMERIC_LOCALE: Record<Language, string> = { ru: 'ru-RU', en: 'en-US', uz: 'en-GB', fa: 'en-GB', pt: 'pt-BR', tr: 'tr-TR', uk: 'uk-UA', de: 'de-DE', es: 'es-ES', pl: 'pl-PL' };

const TIME_SEPARATOR: Record<Language, string> = { ru: ', ', en: ', ', uz: ', ', fa: '، ', pt: ', ', tr: ', ', uk: ', ', de: ', ', es: ', ', pl: ', ' };

const toDate = (date: Date | string): Date => (typeof date === 'string' ? new Date(date) : date);

const clock = (d: Date, lang: Language): string =>
  d.toLocaleTimeString(DATE_LOCALE[lang], { hour: '2-digit', minute: '2-digit' });

export function formatDayMonth(
  date: Date | string,
  lang: Language,
  { utc = false, year = false } = {},
): string {
  const d = toDate(date);
  if (Number.isNaN(d.getTime())) return '';
  if (lang === 'uz' || lang === 'fa') {
    const day = utc ? d.getUTCDate() : d.getDate();
    const month = utc ? d.getUTCMonth() : d.getMonth();
    const dayMonth = lang === 'uz' ? `${day}-${UZ_SHORT_MONTHS[month]}` : `${day} ${FA_MONTHS[month]}`;
    if (!year) return dayMonth;
    const y = utc ? d.getUTCFullYear() : d.getFullYear();
    return lang === 'uz' ? `${dayMonth}, ${y}` : `${dayMonth} ${y}`;
  }
  return d.toLocaleDateString(DATE_LOCALE[lang], {
    day: 'numeric',
    month: 'short',
    year: year ? 'numeric' : undefined,
    timeZone: utc ? 'UTC' : undefined,
  });
}

const UZ_MONTHS = ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr'];

export function formatFullDate(date: Date | string, lang: Language, { utc = false } = {}): string {
  const d = toDate(date);
  if (Number.isNaN(d.getTime())) return '';
  const day = utc ? d.getUTCDate() : d.getDate();
  const month = utc ? d.getUTCMonth() : d.getMonth();
  const year = utc ? d.getUTCFullYear() : d.getFullYear();
  if (lang === 'uz') return `${year}-yil ${day}-${UZ_MONTHS[month]}`;
  if (lang === 'fa') return `${day} ${FA_MONTHS[month]} ${year}`;
  return d.toLocaleDateString(DATE_LOCALE[lang], {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: utc ? 'UTC' : undefined,
  });
}

export function formatDayMonthTime(date: Date | string, lang: Language): string {
  const d = toDate(date);
  if (Number.isNaN(d.getTime())) return '';
  return `${formatDayMonth(d, lang)}${TIME_SEPARATOR[lang]}${clock(d, lang)}`;
}

const MOSCOW_CLOCK = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'Europe/Moscow',
});

export function formatMoscowClock(date: Date | string): string {
  const d = toDate(date);
  if (Number.isNaN(d.getTime())) return '';
  return MOSCOW_CLOCK.format(d);
}

export function formatDateNumeric(
  date: Date | string,
  lang: Language,
  { year = true, time = false } = {},
): string {
  const d = toDate(date);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString(NUMERIC_LOCALE[lang], {
    day: '2-digit',
    month: '2-digit',
    year: year ? 'numeric' : undefined,
  });
  return time ? `${day}${TIME_SEPARATOR[lang]}${clock(d, lang)}` : day;
}
